import React from "react";
import { useDrawer } from "./drawer/DrawerContext";
import Tabs from "./Tabs";
import CopyButton from "./CopyButton";

function BottomDrawer({ className = "" }) {
  const { isOpen, activeTab, data, openDrawer, closeDrawer } = useDrawer();
  const classes = ["ui-bottom-drawer", className, isOpen ? "is-open" : ""]
    .filter(Boolean)
    .join(" ");

  const tabs = [
    { id: "summary", label: "Summary" },
    { id: "events", label: "Events" },
    { id: "audit", label: "Audit" },
    { id: "raw", label: "Raw JSON" },
    { id: "copy", label: "Copy" },
  ];

  const hasData =
    data.summary ||
    (data.events && data.events.length > 0) ||
    (data.audit && data.audit.length > 0) ||
    data.rawJson ||
    (data.copyItems && data.copyItems.length > 0);

  const renderEmpty = () => <div className="ui-bottom-drawer__empty">No details available.</div>;

  const renderSummary = () => {
    if (!data.summary) return renderEmpty();
    return <p className="ui-bottom-drawer__summary">{data.summary}</p>;
  };

  const renderEvents = () => {
    if (!data.events || data.events.length === 0) return renderEmpty();
    return (
      <ul className="ui-bottom-drawer__list">
        {data.events.map((event, idx) => {
          if (event && typeof event === "object") {
            const primary = event.label || event.title || "Event";
            const countSuffix = event.count && event.count > 1 ? ` (${event.count} times)` : "";
            return (
              <li key={event.id || idx} className="ui-bottom-drawer__event">
                <span className="ui-bottom-drawer__event-primary">{primary}{countSuffix}</span>
                {event.secondary && (
                  <span className="ui-bottom-drawer__event-secondary">{event.secondary}</span>
                )}
              </li>
            );
          }
          return <li key={idx}>{event}</li>;
        })}
      </ul>
    );
  };

  const renderAudit = () => {
    if (!data.audit || data.audit.length === 0) return renderEmpty();
    return (
      <ul className="ui-bottom-drawer__list">
        {data.audit.map((entry, idx) => (
          <li key={entry.id || idx}>{entry.label || entry}</li>
        ))}
      </ul>
    );
  };

  const renderRaw = () => {
    if (!data.rawJson) return renderEmpty();
    const raw = typeof data.rawJson === "string" ? data.rawJson : JSON.stringify(data.rawJson, null, 2);
    return <pre className="ui-bottom-drawer__pre">{raw}</pre>;
  };

  const renderCopy = () => {
    if (!data.copyItems || data.copyItems.length === 0) return renderEmpty();
    return (
      <div className="ui-bottom-drawer__copy">
        {data.copyItems.map((item, idx) => (
          <div key={item.label || idx} className="ui-bottom-drawer__copy-row">
            <span>{item.label || "Item"}</span>
            <CopyButton text={item.value || ""} label="Copy" />
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (!hasData) return renderEmpty();
    if (activeTab === "summary") return renderSummary();
    if (activeTab === "audit") return renderAudit();
    if (activeTab === "raw") return renderRaw();
    if (activeTab === "copy") return renderCopy();
    return renderEvents();
  };

  return (
    <div className={classes} aria-live="polite">
      <button
        type="button"
        className="ui-bottom-drawer__toggle"
        onClick={() => (isOpen ? closeDrawer() : openDrawer())}
      >
        Audit &amp; Technical Details
        <span className="ui-bottom-drawer__chevron">{isOpen ? "▾" : "▴"}</span>
      </button>
      {isOpen && (
        <div className="ui-bottom-drawer__panel" role="dialog" aria-modal="false">
          <Tabs tabs={tabs} activeId={activeTab} onChange={(id) => openDrawer(id)} />
          <div className="ui-bottom-drawer__content">{renderContent()}</div>
        </div>
      )}
    </div>
  );
}

export default BottomDrawer;
